
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { MapPin, Wheat, Users } from "lucide-react";

interface UserProfile {
  location: string | null;
  usda_zone: string | null;
}

interface UserCrop {
  commodity: string | null;
  variety: string | null;
  group_name: string | null;
  season: string | null;
  is_primary: boolean;
}

// Use consistent Community interface that matches the database and useTrendsData
interface Community {
  id: string;
  name: string;
  description?: string;
  members_count?: number;
  growth_percentage?: number;
  plant_hardiness_zone?: string;
}

interface TrendsFiltersProps {
  userProfile: UserProfile | null;
  userCrops: UserCrop[];
  communities: Community[];
  selectedState: string;
  selectedCrop: string;
  selectedCommunity: string;
  onStateChange: (state: string) => void;
  onCropChange: (crop: string) => void;
  onCommunityChange: (community: string) => void;
}

export const TrendsFilters = ({
  userProfile,
  userCrops,
  communities,
  selectedState,
  selectedCrop,
  selectedCommunity,
  onStateChange,
  onCropChange,
  onCommunityChange,
}: TrendsFiltersProps) => {
  // Common US states for farming
  const states = [
    "All States",
    "California",
    "Texas", 
    "Iowa",
    "Illinois",
    "Nebraska",
    "Kansas",
    "Minnesota",
    "Indiana",
    "North Carolina",
    "Wisconsin",
    "Ohio",
    "Georgia",
    "Michigan",
    "Arkansas",
    "Florida"
  ];

  // Get unique crops from user preferences
  const availableCrops = ["All Crops", ...userCrops.map(crop => crop.commodity).filter(Boolean)];
  const uniqueCrops = [...new Set(availableCrops)];

  // Get community options
  const communityOptions = ["All Communities", ...communities.map(community => community.name)];

  // Extract state from user location
  const getUserState = () => {
    if (!userProfile?.location) return null;
    // Simple extraction - assumes format like "City, State" or "County, State"
    const parts = userProfile.location.split(',');
    if (parts.length >= 2) {
      return parts[parts.length - 1].trim();
    }
    return null;
  };

  const userState = getUserState();
  const primaryCrop = userCrops.find(crop => crop.is_primary)?.commodity;

  return (
    <Card className="mb-2">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-green-800 flex items-center space-x-2">
          <span>Filter Trends</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {/* User's profile info */}
        {(userState || primaryCrop) && (
          <div className="space-y-1">
            <p className="text-xs text-gray-600">Based on your profile:</p>
            <div className="flex flex-wrap gap-1">
              {userState && (
                <Badge variant="outline" className="flex items-center space-x-1 text-xs px-2 py-0.5">
                  <MapPin size={10} />
                  <span>{userState}</span>
                </Badge>
              )}
              {primaryCrop && (
                <Badge variant="outline" className="flex items-center space-x-1 text-xs px-2 py-0.5">
                  <Wheat size={10} />
                  <span>{primaryCrop}</span>
                </Badge>
              )}
              {userProfile?.usda_zone && (
                <Badge variant="outline" className="text-xs px-2 py-0.5">
                  {userProfile.usda_zone}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700">State/Region</label>
            <Select value={selectedState} onValueChange={onStateChange}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Select state..." />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                {states.map((state) => (
                  <SelectItem key={state} value={state} className="text-sm">
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700">Crop Type</label>
            <Select value={selectedCrop} onValueChange={onCropChange}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Select crop..." />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                {uniqueCrops.map((crop) => (
                  <SelectItem key={crop} value={crop!} className="text-sm">
                    {crop}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700">Community Focus</label>
            <Select value={selectedCommunity} onValueChange={onCommunityChange}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Select community..." />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                {communityOptions.map((community) => (
                  <SelectItem key={community} value={community} className="text-sm">
                    {community}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Community insights */}
        {communities.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-700">Active Communities ({communities.length}):</p>
            <div className="flex flex-wrap gap-1">
              {communities.slice(0, 6).map((community) => (
                <button
                  key={community.id}
                  onClick={() => onCommunityChange(community.name)}
                  className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors flex items-center space-x-1 ${
                    selectedCommunity === community.name
                      ? "bg-green-800 text-white"
                      : "bg-green-100 text-green-800 hover:bg-green-200"
                  }`}
                >
                  <Users size={10} />
                  <span>{community.name}</span>
                  <span className="text-xs opacity-75">({(community.members_count || 0).toLocaleString()})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quick filter buttons for user's preferences grouped by season */}
        {userCrops.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-700">Your Crops by Season:</p>
            {["Early Spring", "Spring-Summer", "Summer-Fall"].map((season) => {
              const seasonCrops = userCrops.filter(crop => crop.season === season);
              if (seasonCrops.length === 0) return null;
              
              return (
                <div key={season} className="space-y-1">
                  <p className="text-xs text-gray-600 font-medium">{season}:</p>
                  <div className="flex flex-wrap gap-1">
                    {seasonCrops.map((crop, index) => (
                      <button
                        key={`${season}-${index}`}
                        onClick={() => onCropChange(crop.commodity || "All Crops")}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                          selectedCrop === crop.commodity
                            ? "bg-green-800 text-white"
                            : "bg-green-100 text-green-800 hover:bg-green-200"
                        }`}
                      >
                        {crop.commodity}
                        {crop.is_primary && " ⭐"}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            
            {/* Show crops without season assigned */}
            {userCrops.some(crop => !crop.season) && (
              <div className="space-y-1">
                <p className="text-xs text-gray-600 font-medium">No Season Assigned:</p>
                <div className="flex flex-wrap gap-1">
                  {userCrops.filter(crop => !crop.season).map((crop, index) => (
                    <button
                      key={`no-season-${index}`}
                      onClick={() => onCropChange(crop.commodity || "All Crops")}
                      className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                        selectedCrop === crop.commodity
                          ? "bg-green-800 text-white"
                          : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                      }`}
                    >
                      {crop.commodity}
                      {crop.is_primary && " ⭐"}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
